"use client"

import { useEffect, useRef } from "react"
import { useCursorContext } from "../components/CursorProvider"

interface UseCursorZoneOptions {
  /** Cursor variant to activate when this zone is in view */
  variant: string
  /**
   * IntersectionObserver threshold (0–1).
   * 0 = triggers as soon as any pixel is visible.
   * 0.5 = triggers when 50% of the element is visible (default).
   */
  threshold?: number
}

/**
 * Returns a ref to attach to any element. When that element scrolls into the
 * viewport past `threshold`, the cursor variant switches automatically.
 */
export function useCursorZone<T extends HTMLElement = HTMLDivElement>({
  variant,
  threshold = 0.5,
}: UseCursorZoneOptions) {
  const ref = useRef<T>(null)
  const { setCursorVariant } = useCursorContext()
  const activeRef = useRef(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !activeRef.current) {
          activeRef.current = true
          setCursorVariant(variant, "push")
        } else if (!entry.isIntersecting && activeRef.current) {
          activeRef.current = false
          setCursorVariant("", "pop")
        }
      },
      { threshold },
    )

    observer.observe(el)

    return () => {
      observer.disconnect()
      if (activeRef.current) {
        activeRef.current = false
        setCursorVariant("", "pop")
      }
    }
  }, [variant, threshold, setCursorVariant])

  return ref
}
